import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Mixpanel } from '../../../services/Mixpanel';
import { useImageUpload } from '../../../hooks/useImageUpload';
import {
  getListOfStartChapters,
  getListOfEndChapters,
} from '../../Homepage/Contexts/DiffitBooksContext';
import {
  extractUrlsFromText,
  extractYoutubeUrlsFromText,
} from '../../Homepage/Utils/GenerationUtils';
import { BookSelectionSection } from './BookSelectionSection';
import { SourceContentForm } from './SourceContentForm';
import { OptionsList } from './OptionsList';
import { ActionButtons } from './ActionButtons';
import { createMemory } from '../../../services/MemoryService';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';

/**
 * Answer recorded when the teacher submits the source-content question with nothing
 * attached. It reaches the input classifier and the outline generator as free text, so it
 * has to read as a declined attachment rather than a provided one.
 */
const NO_SOURCE_CONTENT_ANSWER = 'No source content provided — choose content for me';

/**
 * Displays a single follow-up question, supporting multi-select, open-ended, book selection, and source content question types.
 * @param {Object} question - The question object containing type, options, and metadata.
 * @param {number} questionNumber - The 1-based index of this question in the sequence.
 * @param {boolean} [isLoading=false] - Whether a submission is in progress.
 * @param {Array} initialSelectedOptions - Previously selected option IDs to restore.
 * @param {string} initialOtherText - Previously entered "other" text to restore.
 * @param {Function} onAnswered - Callback when the user submits answers for regular/open-ended questions.
 * @param {Function} onBookConfirmed - Callback when the user confirms a book selection with chapter range.
 * @param {Function} onSourceContentProvided - Callback when the user submits source content (text, PDF, images, URLs).
 * @param {Function} onYoutubeUrlDetected - Callback when a YouTube URL is detected in source content.
 * @param {Function} onBack - Callback to navigate to the previous question.
 * @param {Function} onSkip - Callback to skip the current question.
 */
export const FollowUpQuestions = ({
  question,
  questionNumber,
  isLoading = false,
  initialSelectedOptions,
  initialOtherText,
  onAnswered,
  onBookConfirmed,
  onSourceContentProvided,
  onYoutubeUrlDetected,
  onBack,
  onSkip,
}) => {
  const { isSignedIn } = useDiffitUserContext();

  // Regular option state
  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions || []);
  const [otherText, setOtherText] = useState(initialOtherText || '');
  const [openEndedText, setOpenEndedText] = useState('');
  // "Remember this" — save the answer as a teacher memory (enduring context:
  // class facts or standing preferences that apply to future resources)
  const [rememberAsMemory, setRememberAsMemory] = useState(false);
  const openEndedTextareaRef = useRef(null);

  // Book selection state
  const [bookStartChapterIdx, setBookStartChapterIdx] = useState(-1);
  const [bookEndChapterIdx, setBookEndChapterIdx] = useState(-1);

  // Source content state (lifted so ActionButtons can read upload status)
  const [sourceText, setSourceText] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const sourceTextareaRef = useRef(null);

  // Image upload (shared hook)
  const {
    imageFiles: uploadedImageFiles,
    uploadedImageUrls,
    isUploadingImage,
    imageInputRef,
    maxImages: MAX_IMAGES,
    handleImageInputChange,
    handleImagePaste: handleSourcePaste,
    clearImage: clearUploadedImage,
  } = useImageUpload({ trackingPrefix: 'Unlimited Source Content', setError: setUploadError });

  // Question type checks
  const isBookSelectionQuestion = question?.type === 'book_selection';
  const isOpenEndedQuestion = question?.type === 'open_ended';
  const isSourceContentRequest = question?.type === 'source_content_request';
  const bookData = question?.book_data;

  // Auto-focus textarea for open-ended and source content questions
  useEffect(() => {
    if (isOpenEndedQuestion && openEndedTextareaRef.current) {
      openEndedTextareaRef.current.focus();
    }
  }, [isOpenEndedQuestion, question?.id]);

  useEffect(() => {
    if (isSourceContentRequest && sourceTextareaRef.current) {
      sourceTextareaRef.current.focus();
    }
  }, [isSourceContentRequest, question?.id]);

  // Normalize chapters for book selection
  const normalizedChapters = useMemo(() => {
    if (!bookData?.chapters) return [];
    return bookData.chapters.map((ch, idx) => {
      if (typeof ch === 'object') return ch.name || ch.title || `Chapter ${idx + 1}`;
      return ch;
    });
  }, [bookData?.chapters]);

  const bookStartChapterOptions = useMemo(
    () => getListOfStartChapters(normalizedChapters, bookEndChapterIdx),
    [normalizedChapters, bookEndChapterIdx]
  );

  const bookEndChapterOptions = useMemo(
    () => getListOfEndChapters(normalizedChapters, bookStartChapterIdx),
    [normalizedChapters, bookStartChapterIdx]
  );

  // Option handlers
  const handleOptionClick = (optionId) => {
    if (selectedOptions.includes(optionId)) {
      setSelectedOptions(selectedOptions.filter((id) => id !== optionId));
      if (optionId === 'other') setOtherText('');
    } else {
      setSelectedOptions([...selectedOptions, optionId]);
    }
  };

  const buildAnswerLabels = () => {
    return selectedOptions
      .map((optionId) => {
        if (optionId === 'other') return otherText?.trim() || null;
        const option = question.options?.find((opt) => opt.id === optionId);
        return option?.label || null;
      })
      .filter(Boolean);
  };

  const maybeSaveAnswerAsMemory = (answers) => {
    if (!rememberAsMemory || answers.length === 0) return;
    // Fire-and-forget: a failed memory save must never block the create flow
    createMemory(`${question.question} — ${answers.join(', ')}`, 'follow_up')
      .then(() => Mixpanel.track('Memory Added', { source: 'follow_up', question_id: question.id }))
      .catch(() => null);
  };

  // Continue handler - routes to the right submission path
  const handleContinue = () => {
    if (isBookSelectionQuestion) {
      if (bookStartChapterIdx === -1 || bookEndChapterIdx === -1) return;
      onBookConfirmed({
        book_id: bookData.book_id,
        start_chapter_idx: bookStartChapterIdx,
        end_chapter_idx: bookEndChapterIdx,
      });
      return;
    }

    if (isSourceContentRequest) {
      handleSourceContentSubmit();
      return;
    }

    if (isOpenEndedQuestion) {
      const answers = openEndedText.trim() ? [openEndedText.trim()] : [];
      maybeSaveAnswerAsMemory(answers);
      onAnswered({
        answers,
        selectedOptions,
        otherText,
      });
      return;
    }

    const answers = buildAnswerLabels();
    maybeSaveAnswerAsMemory(answers);
    onAnswered({
      answers,
      selectedOptions,
      otherText,
    });
  };

  const handleSourceContentSubmit = () => {
    const trimmedText = sourceText.trim();

    // Nothing attached: the CTA reads "Choose content for me", so answer the question
    // truthfully instead of reporting source content that was never provided. The
    // source-content path records a fixed "Source content provided" answer, which the
    // input classifier reads as evidence that a text exists to preserve.
    if (!trimmedText && !uploadedFileUrl && uploadedImageUrls.length === 0) {
      Mixpanel.track('Unlimited Source Content Declined', {
        question_number: questionNumber,
      });
      onAnswered({ answers: [NO_SOURCE_CONTENT_ANSWER] });
      return;
    }

    // Check for YouTube URLs
    const youtubeUrls = extractYoutubeUrlsFromText(trimmedText);
    if (youtubeUrls.length > 0 && onYoutubeUrlDetected) {
      Mixpanel.track('YouTube URL Detected', {
        url: youtubeUrls[0],
        source: 'source_content_followup',
      });
      onYoutubeUrlDetected(youtubeUrls[0], (transcript) => {
        let updatedText = trimmedText;
        for (const ytUrl of youtubeUrls) {
          updatedText = updatedText.replace(ytUrl, '').trim();
        }
        setSourceText(updatedText ? `${updatedText}\n\n${transcript}` : transcript);
      });
      return;
    }

    const detectedUrls = extractUrlsFromText(trimmedText);

    Mixpanel.track('Unlimited Source Content Provided', {
      has_pdf: !!uploadedFileUrl,
      has_images: uploadedImageUrls.length > 0,
      image_count: uploadedImageUrls.length,
      has_text: !!trimmedText,
      has_urls: detectedUrls.length > 0,
      url_count: detectedUrls.length,
    });

    onSourceContentProvided({
      pdfUrl: uploadedFileUrl || undefined,
      text: trimmedText || undefined,
      detectedUrls: detectedUrls.length > 0 ? detectedUrls : undefined,
      imageUrls: uploadedImageUrls.length > 0 ? uploadedImageUrls : undefined,
    });
  };

  const handleSkipBook = () => {
    Mixpanel.track('Unlimited Book Skipped in Follow-up', {
      book_title: bookData?.title,
    });
    onAnswered({ answers: [] });
  };

  const handleBack = () => {
    Mixpanel.track('Unlimited Follow-up Back Clicked', { question_number: questionNumber });
    onBack();
  };

  if (!question) return null;

  const questionType = isBookSelectionQuestion
    ? 'book_selection'
    : isSourceContentRequest
      ? 'source_content_request'
      : isOpenEndedQuestion
        ? 'open_ended'
        : 'regular';

  // Fills the app shell and scrolls inside it. Sizing the panel off the viewport instead
  // let a tall question overflow it, onto the white page background.
  return (
    <div className='flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-start pt-[10vh] pb-10 bg-gray-50 px-4'>
      <div className='w-full max-w-xl'>
        {/*
          Back button. The `-ml-2` and `pl-2` are a matched pair: the padding gives the hover
          background room, and the equal negative margin pulls it back so the chevron stays flush
          with the left edge of the question heading and the option cards below. Changing one
          without the other (e.g. "making the padding uniform") silently breaks that alignment.
        */}
        <button
          onClick={handleBack}
          disabled={isLoading}
          className={`flex items-center gap-1 w-fit -ml-2 pl-2 pr-4 py-2 rounded-lg text-sm text-gray-500 mb-6 transition-colors ${
            isLoading ? 'cursor-not-allowed' : 'hover:bg-gray-100 hover:text-gray-700'
          }`}>
          <ChevronLeftIcon className='w-4 h-4' />
          <span>Back</span>
        </button>

        {/* Question */}
        <h2 className='text-2xl font-normal text-gray-900 mb-8'>{question.question}</h2>

        {/* Book Selection UI */}
        {isBookSelectionQuestion && bookData && (
          <BookSelectionSection
            bookData={bookData}
            normalizedChapters={normalizedChapters}
            bookStartChapterIdx={bookStartChapterIdx}
            setBookStartChapterIdx={setBookStartChapterIdx}
            bookEndChapterIdx={bookEndChapterIdx}
            setBookEndChapterIdx={setBookEndChapterIdx}
            bookStartChapterOptions={bookStartChapterOptions}
            bookEndChapterOptions={bookEndChapterOptions}
          />
        )}

        {/* Open-ended textarea */}
        {isOpenEndedQuestion && (
          <div className='mb-8'>
            <textarea
              ref={openEndedTextareaRef}
              value={openEndedText}
              onChange={(e) => setOpenEndedText(e.target.value)}
              placeholder={question.placeholder || 'Add any additional context here...'}
              className='w-full h-[276px] px-5 py-4 rounded-xl border-2 border-gray-200 bg-white text-gray-800 placeholder:text-gray-400 placeholder:italic focus:border-emerald-500 focus:outline-none focus:ring-0 resize-none transition-colors'
            />
          </div>
        )}

        {/* Source Content Request UI */}
        {isSourceContentRequest && (
          <SourceContentForm
            sourceText={sourceText}
            setSourceText={setSourceText}
            onPaste={handleSourcePaste}
            sourceTextareaRef={sourceTextareaRef}
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            uploadedFileUrl={uploadedFileUrl}
            setUploadedFileUrl={setUploadedFileUrl}
            isUploadingFile={isUploadingFile}
            setIsUploadingFile={setIsUploadingFile}
            uploadError={uploadError}
            setUploadError={setUploadError}
            uploadedImageFiles={uploadedImageFiles}
            uploadedImageUrls={uploadedImageUrls}
            isUploadingImage={isUploadingImage}
            imageInputRef={imageInputRef}
            MAX_IMAGES={MAX_IMAGES}
            handleImageInputChange={handleImageInputChange}
            clearUploadedImage={clearUploadedImage}
          />
        )}

        {/* Regular Options */}
        {!isBookSelectionQuestion && !isOpenEndedQuestion && !isSourceContentRequest && (
          <OptionsList
            question={question}
            selectedOptions={selectedOptions}
            onOptionClick={handleOptionClick}
            otherText={otherText}
            disabled={isLoading}
            onOtherTextChange={(e) => setOtherText(e.target.value)}
          />
        )}

        {/* Remember-this checkbox — only on enduring-context questions (class facts / standing preferences) */}
        {!isBookSelectionQuestion &&
          !isSourceContentRequest &&
          question.enduring_context &&
          isSignedIn && (
            <div
              // The list's mb-8 keeps the standard gap above; mb-3 tucks the row
              // against the buttons (same 12px as the options' space-y-3).
              className='mb-3 flex items-center gap-x-2 cursor-pointer'
              onClick={() => setRememberAsMemory(!rememberAsMemory)}>
              <input
                type='checkbox'
                checked={rememberAsMemory}
                onChange={() => setRememberAsMemory(!rememberAsMemory)}
                className='h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600 cursor-pointer'
              />
              <label className='text-sm text-gray-500 pointer-events-none select-none'>
                Remember this for future resources
              </label>
            </div>
          )}

        {/* Action buttons */}
        <ActionButtons
          questionType={questionType}
          isLoading={isLoading}
          onContinue={handleContinue}
          onSkip={onSkip}
          bookChaptersSelected={bookStartChapterIdx !== -1 && bookEndChapterIdx !== -1}
          onSkipBook={handleSkipBook}
          isUploadingFile={isUploadingFile}
          isUploadingImage={isUploadingImage}
          hasSourceContent={Boolean(
            sourceText?.trim() || uploadedFile || uploadedImageFiles?.length
          )}
        />
      </div>
    </div>
  );
};
